import { DonutChart } from '@mantine/charts';
import {
  Box,
  Group,
  Stack,
  Text,
  ThemeIcon,
  useMantineTheme,
} from '@mantine/core';
import { useClipboard } from '@mantine/hooks';
import { IconCircleFilled, IconCopy, IconDownload } from '@tabler/icons-react';
import { Button } from 'design-system/button/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'design-system/tooltip/tooltip';
import { toPng } from 'html-to-image';
import { memo, useCallback, useMemo, useRef } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  type TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';

interface ChartDataPoint {
  label: string;
  value: number;
}

type ValueFormat = 'number' | 'percent' | 'currency';

interface InlineChartProps {
  title: string;
  type: 'bar' | 'line' | 'area' | 'donut';
  data: ChartDataPoint[];
  valueLabel?: string;
  format?: ValueFormat;
}

const CHART_HEIGHT = 160;

const DONUT_COLORS = [
  'chart.8',
  'violet.8',
  'indigo.8',
  'teal.8',
  'yellow.8',
  'orange.8',
  'red.8',
];

const formatValue = (value: number, format?: ValueFormat): string => {
  switch (format) {
    case 'percent':
      return `${value}%`;
    case 'currency':
      return value >= 1_000_000
        ? `$${(value / 1_000_000).toFixed(1)}M`
        : value >= 1_000
          ? `$${(value / 1_000).toFixed(1)}K`
          : `$${value.toLocaleString()}`;
    default:
      return value >= 1_000_000
        ? `${(value / 1_000_000).toFixed(1)}M`
        : value >= 1_000
          ? `${(value / 1_000).toFixed(1)}K`
          : value.toLocaleString();
  }
};

const needsRotation = (data: ChartDataPoint[]): boolean =>
  data.some((d) => d.label.length > 8) || data.length > 6;

const escapeCsvField = (field: string): string =>
  /[,"\n]/.test(field) ? `"${field.replace(/"/g, '""')}"` : field;

const copyChartAsCsv = (
  title: string,
  data: ChartDataPoint[],
  valueLabel?: string,
): string => {
  const header = `Label,${escapeCsvField(valueLabel || 'Value')}`;
  const rows = data.map((d) => `${escapeCsvField(d.label)},${d.value}`);
  return `${title}\n${header}\n${rows.join('\n')}`;
};

type ChartTooltipProps = Omit<TooltipProps<number, string>, 'payload'> & {
  payload?: { payload: ChartDataPoint }[];
  format?: ValueFormat;
  valueLabel?: string;
};

const ChartTooltip = ({
  active,
  payload,
  format,
  valueLabel,
}: ChartTooltipProps) => {
  const theme = useMantineTheme();
  if (!active || !payload?.length) return null;
  const item = payload[0].payload;
  return (
    <Box
      sx={{
        backgroundColor: 'var(--color-surface-elevated-secondary)',
        border: '1px solid var(--color-line-strong)',
        borderRadius: theme.radius.sm,
        padding: '6px 10px',
      }}
    >
      <Text size="xs" c="var(--color-foreground-secondary)" fw={500} mb={2}>
        {item.label}
      </Text>
      <Text size="xs" c="var(--color-foreground)" fw={600}>
        {valueLabel || 'Value'}: {formatValue(item.value, format)}
      </Text>
    </Box>
  );
};

interface CartesianProps {
  data: ChartDataPoint[];
  color: string;
  format?: ValueFormat;
  valueLabel?: string;
}

const CHART_TOKENS = {
  grid: 'var(--color-line)',
  tick: 'var(--color-foreground-secondary)',
};

const xTickProps = (data: ChartDataPoint[], tickFill: string) => {
  const rotated = needsRotation(data);
  return {
    tick: { fill: tickFill, fontSize: 11 },
    // undefined lets recharts auto-thin dense axes; 'preserveStartEnd' would
    // collapse a long series down to just its first and last labels.
    interval: data.length <= 12 ? 0 : undefined,
    ...(rotated && {
      angle: -30,
      textAnchor: 'end' as const,
      height: 50,
    }),
  };
};

const AreaChartInline = ({
  data,
  color,
  format,
  valueLabel,
  fillOpacity,
}: CartesianProps & { fillOpacity: number }) => {
  const { grid, tick } = CHART_TOKENS;
  const tickProps = xTickProps(data, tick);
  return (
    <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
      <AreaChart
        data={data}
        margin={{ top: 6, right: 6, bottom: 0, left: 0 }}
        accessibilityLayer
        aria-label={`${valueLabel ?? 'Value'} chart, ${data.length} data points`}
      >
        <CartesianGrid stroke={grid} vertical={false} horizontal={true} />
        <XAxis
          dataKey="label"
          axisLine={false}
          tickLine={false}
          {...tickProps}
        />
        <YAxis
          width={45}
          axisLine={false}
          tickLine={false}
          tick={{ fill: tick, fontSize: 11 }}
          tickFormatter={(v: number) => formatValue(v, format)}
        />
        <RechartsTooltip
          cursor={{ stroke: tick, strokeDasharray: '3 3' }}
          content={<ChartTooltip format={format} valueLabel={valueLabel} />}
        />
        <Area
          type="monotone"
          dataKey="value"
          stroke={color}
          strokeWidth={2}
          fill={color}
          fillOpacity={fillOpacity}
          isAnimationActive={false}
          dot={false}
          activeDot={{ r: 3, fill: color, stroke: color }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

const BarChartInline = ({
  data,
  color,
  format,
  valueLabel,
}: CartesianProps) => {
  const { grid, tick } = CHART_TOKENS;
  const tickProps = xTickProps(data, tick);
  return (
    <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
      <BarChart
        data={data}
        margin={{ top: 6, right: 6, bottom: 0, left: 0 }}
        accessibilityLayer
        aria-label={`${valueLabel ?? 'Value'} chart, ${data.length} data points`}
      >
        <CartesianGrid stroke={grid} vertical={false} horizontal={true} />
        <XAxis
          dataKey="label"
          axisLine={false}
          tickLine={false}
          {...tickProps}
        />
        <YAxis
          width={45}
          axisLine={false}
          tickLine={false}
          tick={{ fill: tick, fontSize: 11 }}
          tickFormatter={(v: number) => formatValue(v, format)}
        />
        <RechartsTooltip
          cursor={{ fill: 'transparent' }}
          content={<ChartTooltip format={format} valueLabel={valueLabel} />}
        />
        <Bar
          dataKey="value"
          fill={color}
          isAnimationActive={false}
          radius={[2, 2, 0, 0]}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

const DonutChartInline = ({
  data,
  format,
}: {
  data: ChartDataPoint[];
  format?: ValueFormat;
}) => {
  const chartData = data.map((d, i) => ({
    name: d.label,
    value: d.value,
    color: DONUT_COLORS[i % DONUT_COLORS.length],
  }));

  return (
    <Group gap="md" align="center" wrap="nowrap">
      <DonutChart
        data={chartData}
        size={80}
        thickness={14}
        tooltipDataSource="segment"
        valueFormatter={(v) => formatValue(v, format)}
      />
      <Stack gap={4} sx={{ flex: 1, minWidth: 0 }}>
        {chartData.slice(0, 6).map((item) => (
          <Group key={item.name} gap={6} wrap="nowrap" justify="space-between">
            <Group gap={4} wrap="nowrap" sx={{ minWidth: 0, flex: 1 }}>
              <ThemeIcon
                color={item.color}
                variant="transparent"
                size={12}
                sx={{ flexShrink: 0 }}
              >
                <IconCircleFilled size={8} />
              </ThemeIcon>
              <Text
                size="xs"
                c="var(--color-foreground-secondary)"
                truncate
                fw={500}
              >
                {item.name}
              </Text>
            </Group>
            <Text
              size="xs"
              c="var(--color-foreground-tertiary)"
              fw={500}
              sx={{ flexShrink: 0 }}
            >
              {formatValue(item.value, format)}
            </Text>
          </Group>
        ))}
        {chartData.length > 6 && (
          <Text size="xs" c="var(--color-foreground-tertiary)" fw={500}>
            +{chartData.length - 6} more
          </Text>
        )}
      </Stack>
    </Group>
  );
};

export const InlineChart = memo(
  ({ title, type, data, valueLabel, format }: InlineChartProps) => {
    const { colors } = useMantineTheme();
    const chartLineColor = colors.chart[5];
    const chartRef = useRef<HTMLDivElement>(null);
    const clipboard = useClipboard({ timeout: 2000 });

    const handleDownload = useCallback(async () => {
      if (!chartRef.current) return;
      try {
        const dataUrl = await toPng(chartRef.current, {
          pixelRatio: 2,
          // Exported PNGs have no page behind them, so the scheme's resolved
          // canvas color has to be baked in.
          backgroundColor: getComputedStyle(document.body).backgroundColor,
          filter: (node) =>
            !(node instanceof HTMLElement && node.dataset.exportHide),
        });
        const link = document.createElement('a');
        link.download = `${title.toLowerCase().replace(/\s+/g, '-')}.png`;
        link.href = dataUrl;
        link.click();
      } catch {
        // noop
      }
    }, [title]);

    const csvText = useMemo(
      () => copyChartAsCsv(title, data, valueLabel),
      [title, data, valueLabel],
    );

    if (!data || data.length === 0) {
      return (
        <Box
          sx={(theme) => ({
            backgroundColor: 'var(--color-surface-secondary)',
            border: '1px solid var(--color-line-strong)',
            padding: theme.spacing.sm,
          })}
        >
          <Text size="xs" c="var(--color-foreground-tertiary)" fw={500} mb={4}>
            {title}
          </Text>
          <Text size="xs" c="var(--color-foreground-tertiary)" fw={500}>
            No data available
          </Text>
        </Box>
      );
    }

    return (
      <Box
        ref={chartRef}
        sx={(theme) => ({
          backgroundColor: 'var(--color-surface-secondary)',
          border: '1px solid var(--color-line-strong)',
          padding: theme.spacing.sm,
          overflow: 'hidden',
        })}
      >
        <Group justify="space-between" align="center" mb="xs">
          <Text size="xs" c="var(--color-foreground-tertiary)" fw={500}>
            {title}
          </Text>
          <Group gap={4} data-export-hide>
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => clipboard.copy(csvText)}
                  >
                    <IconCopy size={12} stroke={1.5} />
                  </Button>
                }
              />
              <TooltipContent>
                {clipboard.copied ? 'Copied' : 'Copy as CSV'}
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={handleDownload}
                  >
                    <IconDownload size={12} stroke={1.5} />
                  </Button>
                }
              />
              <TooltipContent>Download PNG</TooltipContent>
            </Tooltip>
          </Group>
        </Group>

        {type === 'area' && (
          <AreaChartInline
            data={data}
            color={chartLineColor}
            format={format}
            valueLabel={valueLabel}
            fillOpacity={0.3}
          />
        )}
        {type === 'line' && (
          <AreaChartInline
            data={data}
            color={chartLineColor}
            format={format}
            valueLabel={valueLabel}
            fillOpacity={0.08}
          />
        )}
        {type === 'bar' && (
          <BarChartInline
            data={data}
            color={chartLineColor}
            format={format}
            valueLabel={valueLabel}
          />
        )}
        {type === 'donut' && <DonutChartInline data={data} format={format} />}
      </Box>
    );
  },
);

InlineChart.displayName = 'InlineChart';
